import { flexRender, type Header, type HeaderGroup, type Row, type Table } from '@tanstack/react-table';
import { FiArrowDown, FiArrowUp, FiChevronDown } from 'react-icons/fi';
import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/shared/lib';
import type { DataTableRow, DataTableStatus } from '@/shared/ui/data-table/types';
import { dataTableStyles } from '@/shared/ui/data-table/styles';

/** How many placeholder rows to draw while the page is loading. */
const SKELETON_ROWS = 5;

type TableViewProps<TData extends DataTableRow> = {
  table: Table<TData>;
  caption: string;
  /** Rendered as a `<caption>` inside the table when supplied. */
  tableCaption?: ReactNode;
  selectable: boolean;
  /** Whether sortable headers render a control. */
  sortControls: boolean;
  status: DataTableStatus;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Attach to the scrolling viewport so the height hook can measure it. */
  viewportRef?: (node: HTMLElement | null) => void;
  viewportStyle?: { maxHeight: string };
};

/**
 * The table proper: headers, rows, and the loading and empty states.
 *
 * The scroll container is a focusable named region. Without `tabIndex` a
 * keyboard user cannot scroll a long table at all — the arrow keys go
 * somewhere else and the overflow stays unreachable.
 */
export function TableView<TData extends DataTableRow>({
  table,
  caption,
  tableCaption,
  selectable,
  sortControls,
  status,
  emptyTitle = 'No results',
  emptyDescription,
  viewportRef,
  viewportStyle,
}: TableViewProps<TData>) {
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length + (selectable ? 1 : 0);

  return (
    <div
      ref={viewportRef}
      role="region"
      aria-label={caption}
      tabIndex={0}
      className={dataTableStyles.viewport}
      style={viewportStyle}
    >
      <table className={dataTableStyles.table} aria-busy={status === 'loading' || undefined}>
        {tableCaption ? <caption className={dataTableStyles.tableCaption}>{tableCaption}</caption> : null}
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <HeaderRow
              key={headerGroup.id}
              headerGroup={headerGroup}
              table={table}
              selectable={selectable}
              sortControls={sortControls}
            />
          ))}
        </thead>
        <tbody>
          <BodyRows
            rows={rows}
            selectable={selectable}
            columnCount={columnCount}
            loading={status === 'loading'}
            emptyTitle={emptyTitle}
            emptyDescription={emptyDescription}
          />
        </tbody>
      </table>
    </div>
  );
}

type HeaderRowProps<TData extends DataTableRow> = {
  headerGroup: HeaderGroup<TData>;
  table: Table<TData>;
  selectable: boolean;
  sortControls: boolean;
};

/** One `<tr>` of column headers, optionally led by the select-all checkbox. */
function HeaderRow<TData extends DataTableRow>({
  headerGroup,
  table,
  selectable,
  sortControls,
}: HeaderRowProps<TData>) {
  return (
    <tr>
      {selectable ? (
        <th scope="col" className={dataTableStyles.checkboxCell}>
          <SelectionCheckbox
            label="Select all rows on this page"
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={(next) => table.toggleAllPageRowsSelected(next)}
          />
        </th>
      ) : null}
      {headerGroup.headers.map((header) => (
        <HeaderCell key={header.id} header={header} sortControls={sortControls} />
      ))}
    </tr>
  );
}

type BodyRowsProps<TData extends DataTableRow> = {
  rows: Row<TData>[];
  selectable: boolean;
  columnCount: number;
  loading: boolean;
  emptyTitle: string;
  emptyDescription?: string;
};

/**
 * The body of the table: placeholders, the empty state, or the page of rows.
 *
 * Loading and empty are deliberately different screens. A table that draws its
 * empty state while it is still asking tells the user their filter matched
 * nothing, which is a claim it cannot support yet.
 */
function BodyRows<TData extends DataTableRow>({
  rows,
  selectable,
  columnCount,
  loading,
  emptyTitle,
  emptyDescription,
}: BodyRowsProps<TData>) {
  if (loading) return <SkeletonRows columnCount={columnCount} />;

  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={columnCount} className={dataTableStyles.stateCell}>
          <p className={dataTableStyles.stateTitle}>{emptyTitle}</p>
          {emptyDescription ? <p className={dataTableStyles.stateDescription}>{emptyDescription}</p> : null}
        </td>
      </tr>
    );
  }

  return (
    <>
      {rows.map((row) => (
        <tr
          key={row.id}
          data-selected={row.getIsSelected() ? '' : undefined}
          className={dataTableStyles.row}
        >
          {selectable ? (
            <td className={dataTableStyles.checkboxCell}>
              <SelectionCheckbox
                label={`Select row ${row.id}`}
                checked={row.getIsSelected()}
                onChange={(next) => row.toggleSelected(next)}
              />
            </td>
          ) : null}
          {row.getVisibleCells().map((cell) => {
            const columnDef = cell.column.columnDef as { meta?: { sticky?: 'left' | 'right' } };
            const stickyClass =
              columnDef.meta?.sticky === 'right'
                ? dataTableStyles.stickyCellRight
                : columnDef.meta?.sticky === 'left'
                  ? dataTableStyles.stickyCellLeft
                  : undefined;
            return (
              <td key={cell.id} className={cn(dataTableStyles.cell, stickyClass)}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

/** Placeholder rows drawn while the next page is in flight. */
function SkeletonRows({ columnCount }: { columnCount: number }) {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
        // Rows are positional placeholders, so the index is the identity.
        <tr key={`skeleton-${rowIndex}`} className={dataTableStyles.skeletonRow} aria-hidden="true">
          {Array.from({ length: columnCount }, (_, cellIndex) => (
            <td key={`cell-${cellIndex}`} className={dataTableStyles.cell}>
              <div className={dataTableStyles.skeletonCell} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * One header cell.
 *
 * `aria-sort` is declared on every sortable column — including `none` — so a
 * screen reader can answer "how is this sorted?" without activating the
 * control and re-reading the table.
 */
function HeaderCell<TData extends DataTableRow>({
  header,
  sortControls,
}: {
  header: Header<TData, unknown>;
  sortControls: boolean;
}) {
  const sorted = header.column.getIsSorted();
  const sortable = header.column.getCanSort();
  const ariaSort = sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none';
  const columnDef = header.column.columnDef as { meta?: { sticky?: 'left' | 'right' } };
  // A pinned HEADER cell takes the header row's tint, not the body row's fill:
  // the header row is `bg-surface-sunken`, so a pinned header painted with the
  // body fill would sit as a white notch in an otherwise tinted row.
  const stickyClass =
    columnDef.meta?.sticky === 'right'
      ? dataTableStyles.stickyHeaderCellRight
      : columnDef.meta?.sticky === 'left'
        ? dataTableStyles.stickyHeaderCellLeft
        : undefined;

  if (!sortable) {
    return (
      <th scope="col" className={cn(dataTableStyles.headerCell, stickyClass)}>
        {flexRender(header.column.columnDef.header, header.getContext())}
      </th>
    );
  }

  // A sort the user cannot change is still a sort: announce it, but do not
  // dress the header as a control that would do nothing.
  if (!sortControls) {
    return (
      <th scope="col" className={cn(dataTableStyles.headerCell, stickyClass)} aria-sort={ariaSort}>
        {flexRender(header.column.columnDef.header, header.getContext())}
      </th>
    );
  }

  return (
    <th
      scope="col"
      className={cn(dataTableStyles.headerCell, dataTableStyles.headerCellSortable, stickyClass)}
      aria-sort={ariaSort}
    >
      <button
        type="button"
        className={dataTableStyles.sortButton}
        onClick={header.column.getToggleSortingHandler()}
      >
        <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
        <span
          className={dataTableStyles.sortIcon}
          data-active={sorted === false ? undefined : ''}
        >
          <SortIcon direction={sorted} />
        </span>
      </button>
    </th>
  );
}

/** The direction arrow shown in a sortable header. */
function SortIcon({ direction }: { direction: false | 'asc' | 'desc' }) {
  if (direction === 'asc') return <FiArrowUp size={14} aria-hidden="true" />;
  if (direction === 'desc') return <FiArrowDown size={14} aria-hidden="true" />;
  return <FiChevronDown size={14} aria-hidden="true" />;
}

/**
 * A checkbox that can also be INDETERMINATE.
 *
 * "Select all" on a partially selected page is neither checked nor unchecked,
 * and the DOM has no indeterminate attribute — it is a property set through
 * the element. Without it, the header checkbox lies about the page's state.
 */
function SelectionCheckbox({
  label,
  checked,
  indeterminate,
  onChange,
}: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: (next: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate === true;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 accent-[var(--color-accent)]"
    />
  );
}